import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import dataStore from '../../../store/dataStore.ts'
import { ContactForm } from '../ContactForm.tsx'
import { IContact, IPet } from '../../../interfaces/types.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus'
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import controlStore from '../../../store/controlStore.ts'
import { RemoveContact } from '../../../components/common/RemoveContact/RemoveContact.tsx'
import { PopupPortal } from '../../../components/common/Popup/PopupPortal.tsx'
import { deviceFromPet } from '../../../helpers/dataHelpers.ts'
import { AlertPreferences } from '../../../components/common/AlertPreferences/AlertPreferences.tsx'

export const ContactListMobile: React.FC = () => {
    const contacts = dataStore(s => s.contacts).sort((a, b) => a.firstName > b.firstName ? 1 : -1)
    const pets = dataStore(s => s.pets)
    const navigate = useNavigate()
    const [editingContact, setEditingContact] = React.useState<IContact | null>(null)
    const [isNew, setIsNew] = React.useState(false)
    const [deletingContact, setDeletingContact] = React.useState<IContact | null>(null)
    const [alertContact, setAlertContact] = React.useState<IContact | null>(null)
    const [alertPet, setAlertPet] = React.useState<IPet | null>(null)

    const handleSaved = () => {
        setEditingContact(null)
        setIsNew(false)
    }

    const showForm = editingContact || isNew

    const getPetNames = (petList: any[]) => {
        if (!petList?.length) return []
        return petList.map(petId => {
            const pet = pets.find(p => p._id === petId)
            return pet ? { name: pet.petName, image: pet.imageURL, color: pet.iconColor } : null
        }).filter(Boolean)
    }

    const isDefaultImage = (url: string | undefined) => {
        if (!url) return true
        return url.includes('gibi') || url.includes('logo') || url.includes('default')
    }

    const emptyState = (
        <div className={'flex flex-col items-center gap-4 py-12 text-gray-400'}>
            <FontAwesomeIcon icon={faUserPlus} size={'3x'} />
            <div className={'text-lg'}>No alert contacts yet</div>
            <div className={'text-sm opacity-70'}>Add someone to also receive alerts for your pets</div>
        </div>
    )

    const contactCards = contacts.map((contact: IContact) => {
        const assignedPets = getPetNames(contact.petList)

        return (
            <div key={contact._id}
                 className={'bg-primary text-primary-content rounded-xl p-3 flex flex-col gap-2 overflow-hidden active:opacity-80'}
                 onClick={() => setEditingContact(contact)}
            >
                {/* Name row + delete */}
                <div className={'flex items-center justify-between gap-2'}>
                    <div className={'text-lg font-bold truncate'}>
                        {contact.firstName} {contact.lastName}
                    </div>
                    <button
                        className={'btn btn-ghost btn-sm btn-circle text-primary-content'}
                        onClick={(e) => { e.stopPropagation(); setDeletingContact(contact) }}
                    >
                        <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                </div>

                {/* Contact info */}
                <div className={'flex flex-wrap gap-3 text-sm font-medium opacity-80'}>
                    {contact.mobileNo && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faPhone} className={'opacity-60'} size={'sm'} />
                            <span>{contact.mobileNo}</span>
                        </div>
                    )}
                    {contact.email && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faEnvelope} className={'opacity-60'} size={'sm'} />
                            <span className={'truncate max-w-[10rem]'}>{contact.email}</span>
                        </div>
                    )}
                </div>

                {/* Assigned pets — tap a pet to edit that contact's alerts for it */}
                {assignedPets.length > 0 && (
                    <div className={'grid grid-cols-[auto_1fr] items-start gap-x-2 pt-1'}>
                        <span className={'text-xs font-medium uppercase tracking-wide opacity-60 pt-0.5'}>Alerts:</span>
                        <div className={'flex flex-wrap gap-1.5'}>
                            {assignedPets.map((pet: any) => (
                                <button
                                    type="button"
                                    key={pet.name}
                                    className={'flex items-center gap-1 bg-base-100 text-primary rounded-full px-2 py-0.5 active:ring-2 active:ring-secondary'}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        const fullPet = pets.find(p => p.petName === pet.name)
                                        if (fullPet) {
                                            setAlertContact(contact)
                                            setAlertPet(fullPet)
                                        }
                                    }}
                                >
                                    {isDefaultImage(pet.image) ? (
                                        <div className={'w-4 h-4 rounded-full shrink-0'} style={{ backgroundColor: pet.color || '#ccc' }} />
                                    ) : (
                                        <img src={pet.image} className={'w-4 h-4 rounded-full object-cover shrink-0'} alt={pet.name} />
                                    )}
                                    <span className={'text-xs font-semibold truncate max-w-[5rem]'}>{pet.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* SMS status */}
                <div className={'text-xs font-medium opacity-50'}>
                    SMS: {contact.smsOptIn === 'Opt-in' ? '✓ Opted in' : 'Opted out'}
                </div>
            </div>
        )
    })

    return (
        <div className={'flex flex-col gap-4 w-full h-full overflow-y-auto'}>
            {/* Header */}
            <div className={'sticky top-0 z-10 flex flex-col bg-base-100 pt-4 pb-2 px-3 gap-2'}>
                <div className={'flex items-center justify-between'}>
                    <h1 className={'text-xl font-bold text-primary m-0'}>Alert Contacts</h1>
                    <div className={'flex items-center gap-2'}>
                        <button className={'btn btn-secondary btn-sm gap-1'} onClick={() => setIsNew(true)}>
                            <FontAwesomeIcon icon={faUserPlus} />
                            Add
                        </button>
                        <button className={'btn btn-ghost btn-sm text-primary'} onClick={() => navigate('/' + controlStore.getState().currentPage)}>
                            Close
                        </button>
                    </div>
                </div>
                <p className={'text-xs text-gray-500 m-0 text-left'}>People who also receive alerts for your pets</p>
            </div>

            {/* Cards */}
            <div className={'flex flex-col gap-2 px-3 pb-4'}>
                {contacts.length ? contactCards : emptyState}
            </div>

            {/* Delete confirmation */}
            {deletingContact && (
                <RemoveContact
                    contact={deletingContact}
                    open={!!deletingContact}
                    setOpen={(o) => { if (!o) setDeletingContact(null) }}
                />
            )}

            {/* Edit/Add form in popup */}
            <PopupPortal open={!!showForm}>
                <div className={'w-full flex justify-center max-h-[90vh] overflow-y-auto'}>
                    <ContactForm
                        key={isNew ? 'new' : editingContact?._id}
                        init={isNew ? undefined : (editingContact || undefined)}
                        isCreate={isNew}
                        onSaved={handleSaved}
                        title={'Contact'}
                    />
                </div>
            </PopupPortal>

            {/* Alert preferences for a contact's pet */}
            {alertContact && alertPet && (
                <AlertPreferences
                    open={!!alertContact && !!alertPet}
                    setOpen={(o) => { if (!o) { setAlertContact(null); setAlertPet(null) } }}
                    deviceId={deviceFromPet(alertPet)?._id || ''}
                    petName={alertPet.petName}
                    contactId={alertContact._id}
                    contactName={`${alertContact.firstName} ${alertContact.lastName}`}
                />
            )}
        </div>
    )
}
